export type PrimeSeverity = 'success' | 'info' | 'warn' | 'error' | undefined

/**
 * A credit amount as the billing page shows it: whole credits, rounded toward
 * zero (MYAC-1602). Balances become fractional once float deduction is enabled
 * (MYAC-885). Toward zero, not floor: a positive balance never shows more than
 * the tenant has, and a negative one (a top-up overdrawn by concurrent
 * deductions) shows -3 for -3.2, not -4.
 */
export function wholeCredits(amount: number): number {
    return Math.trunc(amount) || 0 // `|| 0` turns -0 into 0
}

/**
 * Share of the monthly allowance left, from the SAME whole number the page
 * prints next to the bar — so 99.5 / 100 shows "99 / 100" and a 99 % bar,
 * never a full one.
 */
export function recurringPercent(recurring: number, allowance: number): number {
    if (allowance <= 0) return 0
    return Math.max(0, Math.min(100, Math.round((wholeCredits(recurring) / allowance) * 100)))
}

export function tagSeverityForInvoiceStatus(status?: string): PrimeSeverity {
    switch ((status || '').toLowerCase()) {
        case 'paid':
            return 'success'
        case 'open':
        case 'draft':
            return 'info'
        case 'uncollectible':
        case 'void':
            return 'error'
        case 'past_due':
        case 'overdue':
            return 'warn'
        default:
            return undefined
    }
}

export function formatInvoiceMonth(date: Date | undefined, d: (date: Date, opts: Intl.DateTimeFormatOptions) => string): string {
    return date && date instanceof Date && !isNaN(date.getTime())
        ? d(date, { month: 'long', year: 'numeric' })
        : '-'
}

export function formatInvoiceAmount(amountCents: number | null | undefined, currency?: string | null): string {
    if (amountCents === null || amountCents === undefined) return '-'
    const amount = amountCents / 100
    const cur = (currency || 'EUR').toUpperCase()
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: cur,
            maximumFractionDigits: 2,
        }).format(amount)
    } catch {
        const formatted = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(amount)
        return cur === 'EUR' ? `${formatted} €` : `${formatted} ${cur}`
    }
}


